import React, { useEffect, useMemo, useState } from 'react';
import { useGPS } from '../../contexts/GPSContext';
import { useGPSStats } from '../../hooks/useGPSStats';
import SortableTable from '../SortableTable';
import StatTile from '../ui/StatTileNew';
import StatCard from '../ui/StatCard';
import Badge from '../ui/Badge';
import { buildUrlIssue } from '../../utils/url';
import { normalizeStatusCategory } from '../../utils/gpsStatusCategories';

const STATUS_TONE = { NEW: 'blue', INDETERMINATE: 'amber', DONE: 'green' };

const LOCAL_STORAGE_KEY = 'gpsIssuesColumns:v1';
const ALLOWED_KEYS = [
  'created',
  'assets',
  'brand',
  'client_shop_id',
  'issue_key',
  'url',
  'shopsign',
  'intervention_point',
  'request_type',
  'reporter',
  'status',
  'status_date',
  'tiering',
  'problems',
  'assets',
  'furniture',
  'sector',
  'priority',
  'description',
  'duedate',
  'delivery_date',
  'reception_date',
  'request_compliant',
  'intervention_compliant',
  'out_of_time',
  'partial_intervention',
  'out_of_time_reason',
  'intervention_compliant_reason',
  'request_compliant_reason'
];

function formatDate(d, withTime = false) {
  if (!d) return '';
  try {
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return String(d);
    if (withTime) {
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString();
  } catch {
    return String(d);
  }
}

// Two-line date cell: date on top, time in muted gray below (when withTime).
function renderDateCell(d, withTime = false) {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  const dayStr = date.toLocaleDateString();
  if (!withTime) return <span className="text-gray-900 dark:text-gray-200">{dayStr}</span>;
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <span className="block leading-tight">
      <span className="block text-gray-900 dark:text-gray-200">{dayStr}</span>
      <span className="block text-xs text-gray-400 dark:text-gray-500">{timeStr}</span>
    </span>
  );
}

function toLabel(key) {
  const map = {
    created: 'Créé le',
    issue_key: 'Ticket',
    url: 'URL',
    shopsign: 'Enseigne',
    intervention_point: 'Magasin',
    client_shop_id: 'Code Magasin',
    request_type: 'Type de requête',
    status: 'Statut',
    status_date: 'Dernier statut',
    tiering: 'Tiering',
    problems: 'Problèmes',
    assets: 'Éléments',
    furniture: 'Mobilier',
    project_name: 'Projet',
    sector: 'Secteur',
    brand: 'Marque',
    reporter: 'Demandeur',
    priority: 'Priorité',
    duedate: 'Date d\'intervention',
    delivery_date: 'Date d\'envoi éléments',
    reception_date: 'Date de réception éléments',
    request_compliant: 'Demande conforme',
    intervention_compliant: 'Intervention conforme',
    out_of_time: 'Hors délai',
    partial_intervention: 'Intervention partielle',
    out_of_time_reason: 'Raison hors délai',
    intervention_compliant_reason: 'Raison intervention conforme',
    request_compliant_reason: 'Raison demande conforme',
  };

  return map[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Jira URL of an issue — empty for CSV-sourced rows (no Jira ticket) or incomplete keys.
export const issueHref = (row) =>
  (row?.source === 'CSV' ? '' : buildUrlIssue(row?.project_key, row?.issue_key)) || '';

function getDefaultColumnKeys() {
  return [
    'created',
    'issue_key',
    'url',
    'request_type',
    'sector',
    'reporter',
    'shopsign',
    'intervention_point',
    'client_shop_id',
    'status',
    'status_date',
    'furniture',
    'problems',
    'assets',
    'duedate',
    'reception_date',
    'out_of_time',
    'out_of_time_reason',
    'request_compliant',
    'partial_intervention',
    'request_compliant_reason',
    'intervention_compliant',
    'intervention_compliant_reason',
  ];
}

const loadColumnsFromStorage = () => {
  try {
    const dataRaw = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || 'null');
    const defaults = getDefaultColumnKeys();
    const saved = Array.isArray(dataRaw) ? { selected: dataRaw, knownDefaults: dataRaw } : dataRaw;

    if (!saved?.selected?.length) {
      return defaults;
    }

    const selectedSet = new Set(saved.selected);

    const newDefaults = defaults.filter(k => !saved.knownDefaults.includes(k));
    newDefaults.forEach(k => selectedSet.add(k));

    const ordered = [
      ...defaults.filter(k => selectedSet.has(k)),
      ...saved.selected.filter(k => selectedSet.has(k) && !defaults.includes(k)),
    ];

    return ordered;
  } catch {
    return getDefaultColumnKeys();
  }
};

const saveColumnsToStorage = (selectedKeys) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
      selected: selectedKeys,
      knownDefaults: getDefaultColumnKeys(),
    }));
  } catch {}
};

function buildColumns(selectedKeys) {
  const columns = [];

  selectedKeys.forEach(key => {

    const label = toLabel(key);

    if (!label)
      return;

    if (key === 'url') {
      columns.push({
        key,
        label,
        cellClassName: 'w-10 text-center',
        rawValue: issueHref,
        render: (row) => {
          const href = issueHref(row);
          if (!href) return '';
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={href}
              className="inline-flex items-center justify-center text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              aria-label="Ouvrir le ticket"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </a>
          );
        }
      });
      return;
    }

    if (key === 'created' || key === 'status_date' || key === 'duedate' || key === 'delivery_date' || key === 'reception_date') {
      const withTime = !['duedate', 'delivery_date', 'reception_date'].includes(key);
      columns.push({
        key,
        label,
        cellClassName: 'whitespace-nowrap',
        render: (row) => renderDateCell(row[key], withTime)
      });
      return;
    }

    if (key === 'status') {
      columns.push({
        key,
        label,
        render: (row) => {
          if (!row.status) return '';
          const tone = STATUS_TONE[normalizeStatusCategory(row)] || 'gray';
          return <Badge tone={tone} dot>{row.status}</Badge>;
        }
      });
      return;
    }

    if (key === 'intervention_point' || key === 'request_type' || key === 'shopsign') {
      columns.push({
        key,
        label,
        render: (row) => {
          const value = row[key];
          if (value === null || value === undefined || value === '') return '';
          return <span className="block max-w-[200px] truncate" title={String(value)}>{value}</span>;
        }
      });
      return;
    }

    if (key === 'problems' || key === 'assets' || key === 'attachments') {
      columns.push({
        key,
        label,
        render: (row) => Array.isArray(row[key]) ? row[key].join(', ') : (row[key] || '')
      });
      return;
    }

    if (key === 'request_compliant' || key === 'intervention_compliant' || key === 'out_of_time' || key === 'partial_intervention') {
      columns.push({
        key,
        label,
        render: (row) => {
          const value = row[key];
          if (String(value).toLowerCase() === 'true') return 'Oui';
          if (String(value).toLowerCase() === 'false') return 'Non';
          return '';
        }
      });
      return;
    }

    columns.push({ key, label: toLabel(key) });
  });

  return columns;
}

const GPSIssues = () => {
  const gps = useGPS();
  const stats = useGPSStats();

  // Build the list of all potential keys from current issues
  const allKeys = useMemo(() => {
    const keys = new Set();
    (gps.issues || []).slice(0, 200).forEach(issue => {
      Object.keys(issue || {}).forEach(k => keys.add(k));
    });
    // Keep a curated ordering preference
    const preferred = [
      'created','issue_key','url','shopsign','intervention_point','request_type','status','status_date','tiering','problems','assets','furniture','project_name','brand','sector','reporter','priority'
    ];
    
    keys.add('url');
    const merged = [...preferred.filter(k => keys.has(k)), ...[...keys].filter(k => !preferred.includes(k))];

    return merged.filter(k => ALLOWED_KEYS.includes(k));
  }, [gps.issues]);

  // Selected columns persisted in LS, with auto-merge of new default columns
  const [selectedKeys, setSelectedKeys] = useState(loadColumnsFromStorage);

  useEffect(() => {
    saveColumnsToStorage(selectedKeys);
  }, [selectedKeys]);

  const columns = useMemo(() => buildColumns(selectedKeys), [selectedKeys]);

  // Settings modal
  const [openSettings, setOpenSettings] = useState(false);

  const toggleKey = (key) => {
    setSelectedKeys(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      const updated = new Set([...prev, key]);
      const defaults = getDefaultColumnKeys();
      return [
        ...defaults.filter(k => updated.has(k)),
        ...[...updated].filter(k => !defaults.includes(k)),
      ];
    });
  };

  const selectAll = () => setSelectedKeys(allKeys);
  const selectDefaults = () => setSelectedKeys(getDefaultColumnKeys());
  const clearAll = () => setSelectedKeys([]);

  return (
    <div className="space-y-4">
      <StatCard>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatTile
            label="Requêtes conformes"
            value={`${stats.compliantRequestsPercentage}%`}
            subValue={`(${stats.compliantRequests.length})`}
            previousValue={`${stats.previous.compliantRequestsPercentage}%`}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
            color="green"
            size="m"
            description="Pourcentage de requêtes conformes sur la période concernée"
          />
          <StatTile
            label="Interventions conformes"
            value={`${stats.compliantInterventionsPercentage}%`}
            subValue={`(${stats.compliantInterventions.length})`}
            previousValue={`${stats.previous.compliantInterventionsPercentage}%`}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            color="green"
            size="m"
            description="Pourcentage d'interventions conformes sur la période concernée"
          />
        </div>
      </StatCard>

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white"></h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpenSettings(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Colonnes
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-[#1e1e1e] rounded-lg border border-gray-200 dark:border-gray-900 shadow p-4">
        <SortableTable
          data={gps.sensedIssues || []}
          columns={columns}
          emptyMessage="Aucun ticket pour la période, les filtres et l'affichage sélectionnés"
          exportable
          exportFilename="gps_issues.csv"
          globalSearch
        />
      </div>

      {openSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-[#1e1e1e] rounded-lg border border-gray-200 dark:border-gray-900 shadow-xl w-full max-w-3xl mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Colonnes affichées</h4>
              <button
                onClick={() => setOpenSettings(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <button onClick={selectDefaults} className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">Par défaut</button>
              <button onClick={selectAll} className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">Tout</button>
              <button onClick={clearAll} className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">Aucun</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] overflow-auto p-1">
              {allKeys.map(key => (
                <label key={key} className="inline-flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(key)}
                    onChange={() => toggleKey(key)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">{toLabel(key)}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setOpenSettings(false)}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPSIssues;


